"use client";

import { Heart, House as Home } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Craftsmanship() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const infoTiles = [{
    icon: Home,
    label: "Global Ateliers",
    value: "12",
    sublabel: "mastering artisanal techniques"
  }, {
    icon: Heart,
    label: "Ethical Sourcing",
    value: "100%",
    sublabel: "sustainable luxury materials"
  }];
  return <section data-section-id="801" id="craftsmanship-values" className="relative overflow-hidden bg-background py-24 md:py-[140px]">
      {}
      <div className="pointer-events-none absolute right-0 top-1/2 hidden -translate-y-1/2 translate-x-1/4 lg:block xl:translate-x-1/3">
        <div className="h-[520px] w-[520px] xl:h-[640px] xl:w-[640px] bg-cover bg-center opacity-90 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/atelier_textile_detail.webp)]" style={{
        clipPath: "polygon(25% 6.7%, 75% 6.7%, 100% 50%, 75% 93.3%, 25% 93.3%, 0% 50%)"
      }} />
      </div>

      <div className="relative mx-auto w-full max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:gap-10">
          {}
          <div className="lg:col-span-5">
            <div ref={leftRef} className={`lg:sticky lg:top-28 transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <div className="flex items-center gap-3 mb-12">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground [writing-mode:vertical-rl] rotate-180 lg:[writing-mode:horizontal-tb] lg:rotate-0">
                  02 / Our Philosophy
                </span>
                <span className="hidden lg:block h-px w-12 bg-foreground/30" />
              </div>

              <h2 className="font-default text-6xl md:text-7xl lg:text-[84px] leading-[0.85] tracking-tighter text-foreground font-bold">
                Defined by{" "}
                <em className="italic font-medium text-accent">
                  precision
                </em>
              </h2>

              <div className="mt-12 flex items-center gap-4">
                <Badge className="bg-accent text-accent-foreground border-transparent px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] rounded-lg" style={{
                clipPath: "polygon(25% 6.7%, 75% 6.7%, 100% 50%, 75% 93.3%, 25% 93.3%, 0% 50%)"
              }}>
                  <span className="px-2">Lush Standard</span>
                </Badge>
                <span className="font-mono text-xs text-muted-foreground">
                  — The Editorial Noir
                </span>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div ref={rightRef} className={`transition-all duration-700 delay-150 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              {}
              <p className="font-default text-[24px] md:text-[28px] leading-[1.3] font-medium text-foreground">
                We approach fashion as a rigorous architectural discipline. Every silhouette is 
                meticulously engineered with artisanal precision, ensuring that each seam 
                serves as a testament to our uncompromising commitment to structural integrity.
              </p>

              <p className="mt-10 font-default text-[18px] leading-[1.8] text-muted-foreground max-w-2xl">
                Our <span className="text-foreground font-semibold">Atelier Methodology</span>{" "}
                fuses centuries-old tailoring traditions with avant-garde technical mastery. 
                By treating fabric as a medium for structural design, we create garments that 
                are not merely worn, but inhabited—offering a tactile experience of luxury 
                defined by obsessive attention to the smallest detail.
              </p>

              {}
              <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 gap-8">
                {infoTiles.map((tile, i) => {
                const Icon = tile.icon;
                return <div key={i} data-index={i} className="group relative bg-card p-8 border border-border rounded-lg shadow-md transition-all duration-500 hover:border-accent hover:shadow-lg">
                      <div className="flex items-start gap-4 mb-6">
                        <div className="flex items-center justify-center w-10 h-10 bg-accent rounded-lg">
                          <Icon className="w-5 h-5 text-accent-foreground" />
                        </div>
                        <span className="font-mono text-[12px] uppercase tracking-[0.2em] text-muted-foreground pt-2.5">
                          {tile.label}
                        </span>
                      </div>

                      <div className="font-default text-5xl text-foreground leading-none font-bold tracking-tighter">
                        {tile.value}
                      </div>
                      <p className="mt-3 font-default text-sm text-muted-foreground leading-relaxed">
                        {tile.sublabel}
                      </p>

                      {}
                      <div className="mt-6 h-px w-16 bg-accent transition-all duration-500 group-hover:w-24" />
                    </div>;
              })}
              </div>

              {}
              <div className="mt-16 flex flex-col sm:flex-row gap-6">
                <Button asChild size="lg" className="bg-foreground text-background hover:bg-foreground/90 px-8 h-14 font-default text-sm tracking-widest uppercase rounded-lg shadow-md">
                  <Link to="/ShopPage">Explore Collection</Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-border bg-transparent text-foreground hover:bg-accent/10 px-8 h-14 font-default text-sm tracking-widest uppercase rounded-lg shadow-md">
                  <Link to="/BrandStoryPage">Our Story</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}